import { isEqual } from 'lodash-es';
import { type ImgHTMLAttributes, memo, useMemo, useState } from 'react';

import type { CloudflareResizeOptions } from './CloudflareResizeOptions';
import { FALLBACK_IMAGE_WIDTH } from './constants';
import type { SrcSetAnchor } from './types';
import { buildDprSrcSet, getCloudflareDomain, getCloudflareUrl } from './utils';

type CloudflareResponseImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  srcParams?: CloudflareResizeOptions;
  srcSetParams?: Record<SrcSetAnchor, CloudflareResizeOptions>;
};

const CloudflareResponseImage = memo(function CloudflareResponseImage({
  srcParams,
  srcSetParams,
  src,
  fetchPriority,
  ...imageProps
}: CloudflareResponseImageProps) {
  const domain = getCloudflareDomain();

  // React 18 does not recognize the camelCase `fetchPriority` prop and forwards
  // it to the DOM as-is, emitting a console warning. Normalize it to the
  // lowercase `fetchpriority` attribute the browser (and React 18) expect.
  const fetchPriorityAttr = fetchPriority
    ? ({
        fetchpriority: fetchPriority,
      } as unknown as ImgHTMLAttributes<HTMLImageElement>)
    : undefined;

  // Fallback `src` must stay on the CDN. The width-descriptor path passes only
  // `srcSetParams`, so without a resize option getCloudflareUrl would return the
  // raw full-size origin (surfaced via onError or by browsers that skip srcset).
  const srcImageUrl = useMemo(() => {
    if (!src) {
      return src;
    }
    const fallbackParams =
      srcParams ??
      (srcSetParams
        ? { fit: 'contain', width: FALLBACK_IMAGE_WIDTH }
        : undefined);
    return getCloudflareUrl(src, domain, fallbackParams);
  }, [src, domain, JSON.stringify(srcParams), JSON.stringify(srcSetParams)]);

  const srcSetImagesUrl = useMemo(() => {
    if (!src) {
      return undefined;
    }

    if (srcParams?.width) {
      return buildDprSrcSet(src, domain, srcParams);
    }

    if (!srcSetParams) {
      return undefined;
    }

    return Object.entries(srcSetParams)
      .reduce((result, [anchor, params]) => {
        result.push(`${getCloudflareUrl(src, domain, params)} ${anchor}`);
        return result;
      }, [] as string[])
      .join(',');
  }, [src, domain, JSON.stringify(srcParams), JSON.stringify(srcSetParams)]);

  // Which image the srcset candidates already failed for. Comparing against the
  // current URL rather than holding a boolean means a new image starts with a
  // fresh attempt without an effect to reset it.
  const [srcSetFailedFor, setSrcSetFailedFor] = useState<string>();
  const srcSetFailed = Boolean(srcImageUrl) && srcSetFailedFor === srcImageUrl;

  // for testing
  // if (!company.config.cloudflareImages.enabled) {
  //   return <img {...props} />;
  // }
  if (!srcParams?.width && srcSetImagesUrl) {
    imageProps.sizes = imageProps.sizes ?? '100vw';
  }
  return (
    <img
      src={srcImageUrl}
      srcSet={srcSetFailed ? undefined : srcSetImagesUrl}
      {...fetchPriorityAttr}
      {...imageProps}
      onError={() => {
        // Dropping the srcset is the only retry worth making — the browser then
        // falls back to `src` on the same CDN. Re-assigning `src` itself, as
        // this used to, restarts the very request that just failed: assigning
        // the property reloads even when the value is unchanged, and the
        // `onerror = null` guard never applied because React listens through
        // its own event system, not the DOM property. A missing image spun that
        // into an unbounded request loop against the image CDN.
        if (!srcSetFailed && srcSetImagesUrl) {
          setSrcSetFailedFor(srcImageUrl);
        }
      }}
    />
  );
}, isEqual);

export default CloudflareResponseImage;
